import type { DuplicateShopSettingsDto } from "./use-duplicate-shop-settings";
import { useApiMutation } from "./use-api-mutation";

export interface DuplicateSettingsPreviewCarrier {
  code: string;
  title: string | null;
}

export interface DuplicateSettingsPreviewEmailTemplate {
  /** The `returnEmailConfigs` slot. */
  type: string;
  title: string;
}

/** BBS-383. What a duplication would do to the target, nothing written. */
export interface DuplicateShopSettingsPreview {
  /** Sections of the target overwritten as a whole. */
  sections: string[];
  carriers?: {
    deleted: DuplicateSettingsPreviewCarrier[];
    added: DuplicateSettingsPreviewCarrier[];
    kept: DuplicateSettingsPreviewCarrier[];
    defaultCode: string | null;
  };
  overrideRules?: { before: number; after: number };
  routingLocationRules?: { before: number; after: number };
  integrations?: { removed: string[]; replaced: string[]; added: string[] };
  /** BAB-3418. Return email templates, when the preferences are copied. */
  emailTemplates?: {
    /** The source's templates, copied into the target. */
    copied: DuplicateSettingsPreviewEmailTemplate[];
    /** The target's default templates, given the source's content. */
    updated: DuplicateSettingsPreviewEmailTemplate[];
    /** The target keeps its own: the source has none, or the same email. */
    kept: DuplicateSettingsPreviewEmailTemplate[];
    /** The target's return workflows sending an updated template. */
    workflows: string[];
  };
  /** Emails invited on the target. */
  members?: string[];
}

export default function useDuplicateShopSettingsPreview(shopId: string) {
  return useApiMutation(
    "post",
    "/shops/{shopId}/duplicate-settings/preview" as never,
    {
      // The dialog reports refusals itself, with their reason.
      notify: false,
      mapVariables: (requestBody: DuplicateShopSettingsDto) => ({
        params: { path: { shopId } },
        body: requestBody,
      }),
    },
  );
}
